import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import { gql, useLazyQuery } from "@apollo/client";

const useStyles = makeStyles((theme) => ({
  page: { padding: theme.spacing(2) },
  card: { padding: theme.spacing(2) },
  sectionHeader: { marginBottom: theme.spacing(1) },
  actionsRow: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 560,
    borderRadius: 8,
    border: `1px solid ${theme.palette.action.hover}`,
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    background: theme.palette.background.paper,
    zIndex: 1,
  },
}));

// Correct GraphQL query (NO userName)
const LAST_LOGIN_REPORTS = gql`
  query LastLoginReports($name: String!) {
    lastLoginReports(name: $name) {
      email
      lastLoggedTime
    }
  }
`;

// Date-only
const fmtDate = (iso) => { 
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";

  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
}

// CSV export
const toCsv = (rows) => {
  const headers = ["Email", "LastLoginDate"];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) => [r.email, fmtDate(r.lastLoggedTime)].map(esc).join(",")),
  ].join("\n");
};

export const LoginReport = () => {
  const classes = useStyles();

  // THIS is the correct search state
  const [q, setQ] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [runQuery, { data, loading, error }] = useLazyQuery(
    LAST_LOGIN_REPORTS,
    {
      fetchPolicy: "network-only",
    }
  );

  // THIS is the correct way to read rows
  const rows = data?.lastLoginReports ?? [];

  const paged = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const handleSearch = () => {
    const value = q.trim();
    if (!value) {
      alert("Please enter an Email.");
      return;
    }
    setPage(0);

    // THIS is the correct variable name
    runQuery({ variables: { name: value } });
  };

  const handleExport = () => {
    if (!rows.length) return alert("No data to export.");
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `last-login_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <Container maxWidth={false} disableGutters className={classes.page}>
      <Typography variant="h4">Last Login Report</Typography>

      <Paper className={classes.card} style={{ marginTop: 10 }}>
        <Typography variant="subtitle1" className={classes.sectionHeader}>
          Search by Email
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            <div className={classes.actionsRow}>
              <Button
                color="primary"
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : "SEARCH"}
              </Button>

              <Button
                color="primary"
                variant="contained"
                disabled={!rows.length}
                startIcon={<GetAppIcon />}
                onClick={handleExport}
              >
                EXPORT CSV
              </Button>
            </div>
          </Grid>
        </Grid>

        <Divider style={{ marginTop: 16, marginBottom: 12 }} />

        {error && (
          <Typography color="error">
            {error.message}
          </Typography>
        )}

        {/* TABLE */}
        <Toolbar>
          <Typography variant="subtitle2">
            {rows.length} result(s)
          </Typography>
        </Toolbar>

        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.stickyHeader}>
                  <b>Email</b>
                </TableCell>
                <TableCell className={classes.stickyHeader}>
                  <b>Last Logged Date</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((r, idx) => (
                <TableRow key={`${r.email}-${idx}`} hover>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{fmtDate(r.lastLoggedTime)}</TableCell>
                </TableRow>
              ))}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography>No results found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default LoginReport;